import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useUser } from '../../contexts/UserContext';
import { LscAuthGate } from '../hooks/useLscAuthGate';
import { useCommissionerAccess } from '../hooks/useCommissionerAccess';
import { getLscBillingStatus, listLscLeagues, LscApiError } from '../api';
import type { LscLeague } from '../types';
import LoadingState from '../components/LoadingState';
import EmptyState from '../components/EmptyState';
import ErrorBanner from '../components/ErrorBanner';
import MyJoinRequestsPanel from '../components/MyJoinRequestsPanel';
import MyClaimOpportunitiesPanel from '../components/MyClaimOpportunitiesPanel';
import PaidUnboundRecoveryBanner from '../components/PaidUnboundRecoveryBanner';
import { hasConnectedDiscord } from '../discordIdentity';
import {
  filterOwnedLeagues,
  resolvePaidUnboundRecovery,
  type LeagueBillingLinkHint,
} from '../paidUnboundRecovery';
import { shouldShowCreateLeagueNav } from '../commissionerAccess';
import { lscPath, lscJoinInvitePath, lscReturnTo } from '../routing';
import { parseInviteCodeInput } from '../joinInvite';
import {
  NO_LEAGUE_ACTION_CONNECT_DISCORD,
  NO_LEAGUE_ACTION_CREATE_LEAGUE,
  NO_LEAGUE_ACTION_ENTER_INVITE,
  NO_LEAGUE_ACTION_VIEW_PRICING,
  NO_LEAGUE_COMMISSIONER_CARD_DETAIL,
  NO_LEAGUE_COMMISSIONER_CARD_TITLE,
  NO_LEAGUE_COMMISSIONER_SUBSCRIPTION_NOTE,
  NO_LEAGUE_JOIN_CARD_DETAIL,
  NO_LEAGUE_JOIN_CARD_TITLE,
  NO_LEAGUE_LANDING_MEMBER_NOTE,
  NO_LEAGUE_LANDING_MESSAGE,
  NO_LEAGUE_LANDING_TITLE,
} from '../noLeagueLanding';

const LscLeagueListPage: React.FC = () => (
  <LscAuthGate returnTo={lscReturnTo('/leagues')}>
    <LeagueListContent />
  </LscAuthGate>
);

function LeagueListContent() {
  const navigate = useNavigate();
  const { user } = useUser();
  const { presentation: commissionerPresentation } = useCommissionerAccess();
  const showCreateNav = shouldShowCreateLeagueNav(commissionerPresentation);
  const discordConnected = hasConnectedDiscord(user);
  const [leagues, setLeagues] = useState<LscLeague[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviteInput, setInviteInput] = useState('');
  const [inviteInputError, setInviteInputError] = useState<string | null>(null);
  const [billingStatus, setBillingStatus] = useState<Awaited<ReturnType<typeof getLscBillingStatus>> | null>(
    null
  );
  const [leagueBillingHints, setLeagueBillingHints] = useState<LeagueBillingLinkHint[]>([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listLscLeagues();
      const billing = await getLscBillingStatus().catch(() => null);
      const owned = filterOwnedLeagues(data, user?.uid);
      const leagueBillingHints: LeagueBillingLinkHint[] = await Promise.all(
        owned.map(async (league) => ({
          leagueId: league.id,
          leagueName: league.name,
          billingStatus: await getLscBillingStatus(league.id).catch(() => null),
        }))
      );
      setLeagues(data);
      setBillingStatus(billing);
      setLeagueBillingHints(leagueBillingHints);
    } catch (err) {
      setError(err instanceof LscApiError ? err.message : 'Could not load leagues.');
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const ownedLeagues = useMemo(() => filterOwnedLeagues(leagues, user?.uid), [leagues, user?.uid]);
  const recovery = useMemo(
    () =>
      resolvePaidUnboundRecovery(billingStatus, {
        ownedLeagues: ownedLeagues.map((league) => ({ id: league.id, name: league.name })),
        leagueBillingHints,
      }),
    [billingStatus, ownedLeagues, leagueBillingHints]
  );

  if (loading) return <LoadingState message="Loading league workspaces..." />;

  const handleInviteSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const code = parseInviteCodeInput(inviteInput);
    if (!code) {
      setInviteInputError('Enter a valid invite code or link.');
      return;
    }
    setInviteInputError(null);
    navigate(lscJoinInvitePath(code));
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-white">League workspaces</h1>
        <div className="flex flex-wrap gap-2">
          <Link to={lscPath('/join')} className="btn-secondary">
            Enter invite code
          </Link>
          {showCreateNav ? (
            <Link to={lscPath('/leagues/new')} className="btn-primary" data-testid="leagues-header-create">
              {NO_LEAGUE_ACTION_CREATE_LEAGUE}
            </Link>
          ) : (
            <Link to={lscPath('/pricing')} className="btn-secondary" data-testid="leagues-header-pricing">
              {NO_LEAGUE_ACTION_VIEW_PRICING}
            </Link>
          )}
        </div>
      </div>

      <form
        className="card max-w-lsc-narrow space-y-3"
        onSubmit={handleInviteSubmit}
        data-testid="leagues-invite-code-form"
      >
        <div>
          <h2 className="text-sm font-medium text-white">{NO_LEAGUE_JOIN_CARD_TITLE}</h2>
          <p className="text-xs text-secondary-500 mt-1">{NO_LEAGUE_JOIN_CARD_DETAIL}</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            className="input-field flex-1"
            placeholder="sc_inv_… or invite link"
            value={inviteInput}
            onChange={(e) => setInviteInput(e.target.value)}
            data-testid="leagues-invite-code-input"
          />
          <button type="submit" className="btn-primary shrink-0" data-testid="leagues-invite-code-submit">
            Join league
          </button>
        </div>
        {inviteInputError ? (
          <p className="text-sm text-red-300" data-testid="leagues-invite-code-error">
            {inviteInputError}
          </p>
        ) : null}
      </form>

      {error && <ErrorBanner message={error} />}

      <PaidUnboundRecoveryBanner recovery={recovery} onBound={() => void loadData()} />

      {!leagues.length && <MyJoinRequestsPanel compactEmpty />}
      {!leagues.length && <MyClaimOpportunitiesPanel compactEmpty />}

      {!leagues.length ? (
        <EmptyState
          title={NO_LEAGUE_LANDING_TITLE}
          message={NO_LEAGUE_LANDING_MESSAGE}
          action={
            <div className="space-y-4 w-full max-w-2xl mx-auto">
              <div className="grid gap-4 sm:grid-cols-2" data-testid="no-league-landing-actions">
                <div className="card space-y-3 text-left" data-testid="no-league-join-card">
                  <h3 className="text-sm font-semibold text-white">{NO_LEAGUE_JOIN_CARD_TITLE}</h3>
                  <p className="text-xs text-secondary-500">{NO_LEAGUE_JOIN_CARD_DETAIL}</p>
                  <Link
                    to={lscPath('/join')}
                    className="btn-primary text-sm inline-flex"
                    data-testid="enter-invite-code-button"
                  >
                    {NO_LEAGUE_ACTION_ENTER_INVITE}
                  </Link>
                </div>
                <div className="card space-y-3 text-left" data-testid="no-league-commissioner-card">
                  <h3 className="text-sm font-semibold text-white">{NO_LEAGUE_COMMISSIONER_CARD_TITLE}</h3>
                  <p className="text-xs text-secondary-500">{NO_LEAGUE_COMMISSIONER_CARD_DETAIL}</p>
                  <p className="text-xs text-secondary-500">{NO_LEAGUE_COMMISSIONER_SUBSCRIPTION_NOTE}</p>
                  {showCreateNav ? (
                    <Link
                      to={lscPath('/leagues/new')}
                      className="btn-secondary text-sm inline-flex"
                      data-testid="create-league-landing-link"
                    >
                      {NO_LEAGUE_ACTION_CREATE_LEAGUE}
                    </Link>
                  ) : (
                    <Link
                      to={lscPath('/pricing')}
                      className="btn-secondary text-sm inline-flex"
                      data-testid="view-pricing-landing-link"
                    >
                      {NO_LEAGUE_ACTION_VIEW_PRICING}
                    </Link>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-3">
                {discordConnected ? (
                  <span
                    className="text-sm text-emerald-200"
                    data-testid="connect-discord-landing-connected"
                  >
                    Discord connected
                  </span>
                ) : (
                  <Link
                    to={lscPath('/settings')}
                    className="btn-secondary"
                    data-testid="connect-discord-landing-link"
                  >
                    {NO_LEAGUE_ACTION_CONNECT_DISCORD}
                  </Link>
                )}
              </div>
              <p className="text-xs text-secondary-500 text-center">{NO_LEAGUE_LANDING_MEMBER_NOTE}</p>
            </div>
          }
        />
      ) : (
        <div className="lsc-split-when-wide">
        <ul className="lsc-league-grid">
          {leagues.map((league) => (
            <li key={league.id}>
              <Link
                to={lscPath(`/leagues/${league.id}`)}
                className="card block hover:border-primary-500 transition-colors"
              >
                <h2 className="text-lg font-semibold text-white">{league.name}</h2>
                <p className="text-sm text-secondary-400 mt-1">
                  {league.game_label ?? league.game_key ?? 'Competition league'}
                </p>
                <p className="text-xs text-secondary-500 mt-2">/{league.slug}</p>
              </Link>
            </li>
          ))}
        </ul>
        </div>
      )}
    </div>
  );
}

export default LscLeagueListPage;
